// Credit cost model + pure balance arithmetic.
//
// Credits are INTEGERS (1 credit = 1 AI image generation). Text/vision "tips"
// are included with the tier but rate-limited rather than credit-charged, so
// they cost 0 — this keeps the Postgres ledger integer-only (see Architecture §5)
// and the UX simple ("1 credit = 1 image"). These helpers mirror the
// server-side RPC for optimistic UI; the Edge Function remains authoritative.

import type { AiOp, AiQuality } from './types';

export const CREDIT_COST: Record<AiOp, number> = {
  transform: 1,
  upscale: 1,
  vision: 0,
  idea: 0,
};

/**
 * Quality-aware cost for the optimistic client guard — MUST mirror the server's
 * costFor() in supabase/functions/ai-generate/index.ts. A premium transform
 * costs 2 credits (Google Gemini), standard 1 (Seedream). The Edge Function
 * remains authoritative; this only avoids a doomed round trip when broke.
 */
export function costOfQuality(op: AiOp, quality: AiQuality): number {
  if (op === 'transform') return quality === 'premium' ? 2 : 1;
  return CREDIT_COST[op];
}

export class InsufficientCreditsError extends Error {
  constructor(
    public readonly required: number,
    public readonly available: number,
  ) {
    super(`insufficient_credits: need ${required}, have ${available}`);
    this.name = 'InsufficientCreditsError';
  }
}

export function costOf(op: AiOp): number {
  return CREDIT_COST[op];
}

export function canAfford(balance: number, op: AiOp): boolean {
  return balance >= costOf(op);
}

/** Returns the new balance after consuming an op, or throws (never goes negative). */
export function applyConsume(balance: number, op: AiOp): number {
  const cost = costOf(op);
  if (balance < cost) {
    throw new InsufficientCreditsError(cost, balance);
  }
  return balance - cost;
}

/** Returns the new balance after refunding an op's cost (no-op for free ops). */
export function applyRefund(balance: number, op: AiOp): number {
  return balance + costOf(op);
}
